<script>
import { KINDS, FRAMES, payload, compose, composeSVG } from './qr.js';

const COLORS = ['111111', 'E52E4C', '1D4ED8', '047857', '6D28D9'];
const BACKS = ['FFFFFF', 'F4F5F7', 'FFF7E6', null];
const SIZES = { S: 4, M: 8, L: 16 };
const PREVIEW_SCALE = 6;

function samples() {
    const out = {};
    for (const k of KINDS) { out[k.id] = {}; for (const f of k.fields) out[k.id][f.id] = f.sample; }
    return out;
}

export default {
    data() {
        return {
            kinds: KINDS, frames: FRAMES, colors: COLORS, backs: BACKS,
            kind: 'website', values: samples(),
            fg: '111111', bg: 'FFFFFF', frame: 'label', text: 'Scan me!',
            logo: null, logoUrl: '', logoName: '',
            size: 'L', format: 'PNG',
            encoders: null, drawn: false, error: '', status: '',
            base: (window.BARKODER_RUNTIME && window.BARKODER_RUNTIME.HOME_URL) || '/',
        };
    },
    computed: {
        current() { return this.kinds.find((k) => k.id === this.kind); },
        content() { return payload(this.kind, this.values[this.kind]); },
        bad() { return this.error !== ''; },
    },
    watch: {
        values: { deep: true, handler() { this.draw(); } },
        kind() { this.draw(); },
        fg() { this.draw(); }, bg() { this.draw(); }, frame() { this.draw(); }, text() { this.draw(); }, logo() { this.draw(); },
    },
    async mounted() {
        this.encoders = await import('./encoders.js');
        if (document.fonts && document.fonts.load) { try { await document.fonts.load('700 20px PlusJakartaSans'); } catch (e) { /* the fallback face draws it */ } }
        this.draw();
    },
    methods: {
        qrOptions(scale) {
            const o = { text: this.content, scale, eclevel: this.logo ? 'H' : 'M', barcolor: this.fg, paddingwidth: this.frame === 'none' || this.frame === 'badge' ? 4 : 2, paddingheight: this.frame === 'none' || this.frame === 'badge' ? 4 : 2 };
            if (this.bg) o.backgroundcolor = this.bg; else if (this.frame === 'label' || this.frame === 'top') o.backgroundcolor = 'FFFFFF';
            return o;
        },
        look() { return { frame: this.frame, text: this.text.trim(), fg: this.fg, bg: this.bg, logo: this.logo }; },
        draw() {
            this.status = '';
            this.error = this.content ? '' : 'Fill in the ' + this.current.fields[0].label.toLowerCase() + '.';
            if (this.error || !this.encoders || !this.$refs.canvas) return;
            try {
                const tmp = document.createElement('canvas');
                this.encoders.toCanvas(tmp, this.qrOptions(PREVIEW_SCALE));
                compose(this.$refs.canvas, tmp, this.look());
                this.drawn = true;
            } catch (e) {
                this.error = String(e.message || e).replace(/^bwipp?\.[\w.]+:\s*/, '');
            }
        },
        pickLogo(e) {
            const file = e.target.files && e.target.files[0];
            if (!file) return;
            if (file.size > 2 * 1024 * 1024) { this.status = 'The logo can be 2 MB at most'; return; }
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.onload = () => { this.logoUrl = reader.result; this.logoName = file.name; this.logo = img; };
                img.onerror = () => { this.status = 'That file is not an image'; };
                img.src = reader.result;
            };
            reader.readAsDataURL(file);
        },
        removeLogo() { this.logo = null; this.logoUrl = ''; this.logoName = ''; if (this.$refs.logo) this.$refs.logo.value = ''; },
        file() { return 'qr-code-' + this.kind + '.' + this.format.toLowerCase(); },
        async blob() {
            const scale = SIZES[this.size];
            if (this.format === 'SVG') {
                const svg = this.encoders.toSVG(this.qrOptions(scale));
                const m = svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
                const look = { ...this.look(), logo: this.logo ? this.logoUrl : null };
                return new Blob([composeSVG(svg, m ? Number(m[1]) : 300, look)], { type: 'image/svg+xml' });
            }
            const tmp = document.createElement('canvas');
            this.encoders.toCanvas(tmp, this.qrOptions(scale));
            const out = compose(document.createElement('canvas'), tmp, this.look());
            return new Promise((ok) => out.toBlob(ok, 'image/png'));
        },
        async download() {
            if (this.bad || !this.encoders) return;
            const url = URL.createObjectURL(await this.blob());
            const a = document.createElement('a');
            a.href = url; a.download = this.file();
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
        },
        async copy() {
            if (this.bad || !this.encoders) return;
            try {
                if (this.format === 'PNG' && window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
                    await navigator.clipboard.write([new ClipboardItem({ 'image/png': await this.blob() })]);
                } else {
                    await navigator.clipboard.writeText(await (await this.blob()).text());
                }
                this.status = 'Copied';
            } catch (e) {
                this.status = 'Copy is not allowed here; use Download';
            }
            setTimeout(() => { this.status = ''; }, 2500);
        },
    },
};
</script>

<template>
    <div class="gen-tool qr-tool">
        <div class="gen-tool__form">
            <div class="gen-field">
                <span class="gen-label">Content</span>
                <div class="qr-tabs" role="tablist">
                    <button v-for="k in kinds" :key="k.id" type="button" role="tab" class="qr-tab" :class="{ 'is-on': kind === k.id }" :aria-selected="kind === k.id ? 'true' : 'false'" @click="kind = k.id">{{ k.label }}</button>
                </div>
            </div>
            <div class="qr-fields" :class="'qr-fields--' + kind">
                <div v-for="f in current.fields" :key="kind + f.id" class="gen-field" :class="{ 'qr-field--wide': f.type === 'textarea' || current.fields.length === 1 }">
                    <template v-if="f.type === 'check'">
                        <label class="qr-check"><input type="checkbox" v-model="values[kind][f.id]"> {{ f.label }}</label>
                    </template>
                    <template v-else>
                        <label class="gen-label" :for="'qr-' + f.id">{{ f.label }}</label>
                        <select v-if="f.type === 'select'" :id="'qr-' + f.id" v-model="values[kind][f.id]" class="gen-input"><option v-for="o in f.options" :key="o">{{ o }}</option></select>
                        <textarea v-else-if="f.type === 'textarea'" :id="'qr-' + f.id" v-model="values[kind][f.id]" class="gen-input qr-textarea" rows="2"></textarea>
                        <input v-else :id="'qr-' + f.id" v-model="values[kind][f.id]" class="gen-input" :type="f.type === 'url' || f.type === 'email' || f.type === 'tel' ? f.type : 'text'" autocomplete="off">
                    </template>
                </div>
            </div>
            <div class="qr-design">
                <div class="gen-field">
                    <span class="gen-label">Color</span>
                    <div class="qr-swatches">
                        <button v-for="c in colors" :key="c" type="button" class="qr-swatch" :class="{ 'is-on': fg === c }" :style="{ background: '#' + c }" :aria-label="'Color #' + c" @click="fg = c"></button>
                        <label class="qr-swatch qr-swatch--pick" :class="{ 'is-on': !colors.includes(fg) }" aria-label="Pick a color"><input type="color" :value="'#' + fg" @input="fg = $event.target.value.slice(1).toUpperCase()"></label>
                    </div>
                </div>
                <div class="gen-field">
                    <span class="gen-label">Background</span>
                    <div class="qr-swatches">
                        <button v-for="c in backs" :key="String(c)" type="button" class="qr-swatch" :class="{ 'is-on': bg === c, 'qr-swatch--none': c === null }" :style="c ? { background: '#' + c } : {}" :aria-label="c ? 'Background #' + c : 'Transparent background'" @click="bg = c"></button>
                    </div>
                </div>
            </div>
            <div class="gen-field">
                <span class="gen-label">Frame</span>
                <div class="qr-frames">
                    <button v-for="f in frames" :key="f.id" type="button" class="qr-frame" :class="['qr-frame--' + f.id, { 'is-on': frame === f.id }]" @click="frame = f.id"><i></i><span>{{ f.label }}</span></button>
                </div>
            </div>
            <div class="qr-extras">
                <div class="gen-field">
                    <label class="gen-label" for="qr-text">Frame text</label>
                    <input id="qr-text" v-model="text" class="gen-input" type="text" maxlength="24" :disabled="frame === 'none'">
                </div>
                <div class="gen-field">
                    <span class="gen-label">Logo</span>
                    <label v-if="!logo" class="qr-logo"><input ref="logo" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" @change="pickLogo">Upload logo</label>
                    <div v-else class="qr-logo qr-logo--on"><span>{{ logoName }}</span><button type="button" aria-label="Remove the logo" @click="removeLogo">×</button></div>
                </div>
            </div>
            <div class="qr-out-options">
                <div class="gen-opt"><span class="gen-label">Size</span><div class="gen-seg"><button v-for="s in ['S', 'M', 'L']" :key="s" type="button" class="gen-chip" :class="{ 'is-on': size === s }" @click="size = s">{{ s }}</button></div></div>
                <div class="gen-opt"><span class="gen-label">Format</span><div class="gen-seg"><button v-for="f in ['PNG', 'SVG']" :key="f" type="button" class="gen-chip" :class="{ 'is-on': format === f }" @click="format = f">{{ f }}</button></div></div>
            </div>
        </div>
        <div class="gen-tool__out">
            <div class="gen-preview qr-preview" :class="{ 'is-faded': bad }" :data-payload="content">
                <canvas ref="canvas" class="gen-preview__code" :class="{ 'is-drawn': drawn }" role="img" :aria-label="'QR code for ' + content"></canvas>
                <img v-if="!drawn" class="gen-preview__code qr-preview__sample" :src="base + 'themes/barkoder/img/generator/qr-frame-sample.svg'" alt="">
            </div>
            <p class="gen-out-note" :class="{ 'is-bad': bad }" aria-live="polite">{{ error || status || 'Drawn in your browser as you type' }}</p>
            <div class="gen-actions">
                <button type="button" class="button link red" :disabled="bad" @click="download">Download {{ format }}</button>
                <button type="button" class="button link outline gen-copy" :disabled="bad" @click="copy">Copy</button>
            </div>
            <a class="gen-scan" :href="base + 'free-barcode-scanner'">Test it with the free barcode scanner →</a>
        </div>
    </div>
</template>
